/**
 * The app's picture vocabulary. Stored as keys in Firestore; the web app maps them to icons.
 * `suggestIcon` picks a picture from a German title, so creating something stays one field.
 */
export const iconKeys = [
  'check',
  'shirt',
  'tooth',
  'backpack',
  'bed',
  'broom',
  'book',
  'fork',
  'trash',
  'plant',
  'car',
  'dish',
  'toy',
  'moon',
  'tv',
  'pizza',
  'icecream',
  'film',
  'game',
  'ball',
  'horse',
  'doctor',
  'tree',
  'school',
  'cake',
  'exam',
  'meeting',
  'calendar',
  'cart',
  'money',
  'dog',
  'cat',
  'music',
  'swim',
  'bike',
  'shower',
  'laundry',
  'gift',
  'plane',
  'phone',
  'paint',
  'heart',
  'star',
  'recycle',
  'paper',
  'bio',
  'leaf',
  'warning',
] as const;
export type IconKey = (typeof iconKeys)[number];

const RULES: [RegExp, IconKey][] = [
  [/gelb|wertstoff|plastik/i, 'recycle'],
  [/papier|altpapier|blau/i, 'paper'],
  [/bio|kompost/i, 'bio'],
  [/rest|tonne|müll|abfall/i, 'trash'],
  [/schadstoff/i, 'warning'],
  [/zahn|zähne/i, 'tooth'],
  [/anzieh|kleid|klamott|schlafanzug/i, 'shirt'],
  [/ranzen|rucksack|tasche|turnbeutel/i, 'backpack'],
  [/bett/i, 'bed'],
  [/aufräum|sauge|putz|fegen|staub/i, 'broom'],
  [/hausaufgabe|lesen|buch|lernen|vokabel/i, 'book'],
  [/arbeit|test|klausur/i, 'exam'],
  [/tisch|essen|koch|frühstück|abendbrot|mittag/i, 'fork'],
  [/spülmaschine|abwasch|spülen/i, 'dish'],
  [/blume|gieß|garten|rasen|pflanz/i, 'plant'],
  [/auto|fahr|tank/i, 'car'],
  [/spielzeug|lego/i, 'toy'],
  [/fußball|handball|training|sport|basketball|tennis/i, 'ball'],
  [/reit|pferd/i, 'horse'],
  [/arzt|zahnarzt|kinderarzt|impf|ärztin/i, 'doctor'],
  [/ausflug|wald|park|wandern|zoo/i, 'tree'],
  [/schule|kita|kindergarten|unterricht/i, 'school'],
  [/geburtstag|party|feier/i, 'cake'],
  [/elternabend|treffen|besprechung|chor|verein/i, 'meeting'],
  [/einkauf|kaufen|milch|brot|supermarkt/i, 'cart'],
  [/überweis|bezahl|geld|taschengeld|rechnung/i, 'money'],
  [/hund|gassi/i, 'dog'],
  [/katze/i, 'cat'],
  [/musik|klavier|gitarre|flöte|singen/i, 'music'],
  [/schwimm|bad/i, 'swim'],
  [/fahrrad|rad/i, 'bike'],
  [/dusch|bade/i, 'shower'],
  [/wäsche|wasch/i, 'laundry'],
  [/geschenk|wunsch/i, 'gift'],
  [/urlaub|flug|reise/i, 'plane'],
  [/handy|anruf|telefon/i, 'phone'],
  [/mal|bastel|kunst/i, 'paint'],
  [/medien|fernseh|film|kino/i, 'tv'],
  [/eis/i, 'icecream'],
  [/pizza/i, 'pizza'],
  [/spiel|konsole|zocken/i, 'game'],
];

/**
 * English titles (1.19). Whole words only and checked first: the German rules look for word
 * parts ("rest" in "Restmüll" would turn "Forest walk" into a bin).
 */
const RULES_EN: [RegExp, IconKey][] = [
  [/\b(recycling|yellow bags?|plastics?)\b/i, 'recycle'],
  [/\b(paper|cardboard)\b/i, 'paper'],
  [/\b(food waste|compost|garden waste)\b/i, 'bio'],
  [/\b(bins?|rubbish|general waste)\b/i, 'trash'],
  [/\bhazardous\b/i, 'warning'],
  [/\b(teeth|tooth)\b/i, 'tooth'],
  [/\b(get dressed|clothes|pyjamas)\b/i, 'shirt'],
  [/\b(school ?bag|backpack|rucksack|pe kit)\b/i, 'backpack'],
  [/\b(bed|bedtime)\b/i, 'bed'],
  [/\b(tidy|hoover|vacuum|clean|dust|sweep)/i, 'broom'],
  [/\b(homework|read|reading|books?|spellings?|revise|revision)\b/i, 'book'],
  [/\b(test|exam)s?\b/i, 'exam'],
  [/\b(set the table|lay the table|dinner|lunch|breakfast|cook|cooking)\b/i, 'fork'],
  [/\b(dishwasher|washing up|dishes)\b/i, 'dish'],
  [/\b(plants?|garden|lawn|flowers?)\b/i, 'plant'],
  [/\b(car|drive|petrol)\b/i, 'car'],
  [/\b(toys?|lego)\b/i, 'toy'],
  [/\b(football|rugby|cricket|training|sports?|basketball|tennis|netball|hockey)\b/i, 'ball'],
  [/\b(riding|horse|pony)\b/i, 'horse'],
  [/\b(doctor|dentist|gp|vaccinations?|jab)\b/i, 'doctor'],
  [/\b(day out|trip|forest|woods|park|walk|hike|zoo)\b/i, 'tree'],
  [/\b(school|nursery|class)\b/i, 'school'],
  [/\b(birthday|party)\b/i, 'cake'],
  [/\b(parents'? evening|meeting|choir|club)\b/i, 'meeting'],
  [/\b(shopping|buy|milk|bread|supermarket|groceries)\b/i, 'cart'],
  [/\b(pay|transfer|money|pocket money|bill)\b/i, 'money'],
  [/\b(dog|walkies)\b/i, 'dog'],
  [/\bcat\b/i, 'cat'],
  [/\b(music|piano|guitar|flute|recorder|singing)\b/i, 'music'],
  [/\bswim/i, 'swim'],
  [/\b(bike|cycling)\b/i, 'bike'],
  [/\b(shower|bath)\b/i, 'shower'],
  [/\b(laundry|washing)\b/i, 'laundry'],
  [/\b(present|gift|wish)\b/i, 'gift'],
  [/\b(holiday|flight|travel)\b/i, 'plane'],
  [/\b(phone|call)\b/i, 'phone'],
  [/\b(draw|drawing|paint|painting|crafts?)\b/i, 'paint'],
  [/\b(screen time|tv|film|cinema|movie)\b/i, 'tv'],
  [/\bice cream\b/i, 'icecream'],
  [/\bpizza\b/i, 'pizza'],
  [/\b(games?|console)\b/i, 'game'],
];

export function suggestIcon(title: string, fallback: IconKey = 'check'): IconKey {
  for (const [re, key] of RULES_EN) if (re.test(title)) return key;
  for (const [re, key] of RULES) if (re.test(title)) return key;
  return fallback;
}
